<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
    mobil: Object,
    setting: Object,
});

const waNumber = props.setting?.setting_no_wa || '6281234567890';
// Encode hanya text pesannya saja, bukan URL-nya
const waMessage = encodeURIComponent(`Halo, saya tertarik dengan ${props.mobil?.mobil_nama}. Mohon informasi lebih lanjut.`);
const waLink = computed(() => `https://wa.me/${waNumber}?text=${waMessage}`);

const imageUrl = computed(() => {
    return props.mobil?.mobil_foto 
        ? `/storage/mobil/${props.mobil.mobil_foto}`
        : '/storage/logo/logo.webp';
});

const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID').format(value || 0);
};
</script>

<template>
    <div class="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden hover:border-orange-400 hover:shadow-xl transition-all duration-300 flex flex-col h-full">
        <!-- Image -->
        <div class="relative aspect-[4/3] bg-white dark:bg-gray-700/50 overflow-hidden border-b border-gray-100 dark:border-gray-700">
            <!-- Custom Pill for Type (Tag) -->
            <div v-if="mobil.jenis_mobil" class="absolute top-4 left-4 z-10">
                <span class="bg-blue-600 text-white text-[10px] uppercase font-bold px-3 py-1 rounded-full shadow-md flex items-center gap-1">
                    <i class="fa fa-tag text-[9px]"></i> {{ mobil.jenis_mobil.jenis_nama }}
                </span>
            </div>

            <img 
                :src="imageUrl" 
                :alt="mobil.mobil_nama" 
                class="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500" 
                loading="lazy" 
            />
        </div>

        <!-- Info Mobil -->
        <div class="p-4 flex flex-col flex-grow bg-white dark:bg-gray-800 transition-colors">
            <!-- Title -->
            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-1 truncate group-hover:text-blue-700 transition-colors" :title="mobil.mobil_nama">
                <Link :href="`/mobil/${mobil.mobil_slug}`">{{ mobil.mobil_nama }}</Link>
            </h3>
            
            <!-- Description -->
            <p class="text-gray-500 text-xs mb-4 line-clamp-2 leading-relaxed min-h-[2.5em]">
                {{ (mobil.mobil_deskripsi || 'Cocok untuk perjalanan dinas, wisata keluarga, atau wedding car di Banyuwangi.').replace(/<[^>]*>?/gm, '') }}
            </p>

            <!-- Price List (Homepage Style) -->
            <div class="mb-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600 rounded-xl overflow-hidden mt-auto">
                <div v-if="mobil.mobil_harga_sewa" class="flex items-center justify-between text-sm p-3">
                    <span class="text-gray-700 dark:text-gray-300 flex items-center gap-2 font-medium">
                        <span class="w-6 h-6 bg-orange-100 dark:bg-gray-600 flex items-center justify-center text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-gray-500 rounded-lg"><i class="fa fa-key text-xs"></i></span>
                        Lepas Kunci
                    </span>
                    <span class="font-bold text-gray-900 dark:text-white">Rp {{ formatCurrency(mobil.mobil_harga_sewa) }}</span>
                </div>
                <div v-if="mobil.mobil_harga_include" class="flex items-center justify-between text-sm p-3">
                    <span class="text-gray-700 dark:text-gray-300 flex items-center gap-2 font-medium">
                        <span class="w-6 h-6 bg-blue-100 dark:bg-gray-600 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-gray-500 rounded-lg"><i class="fa fa-user text-xs"></i></span>
                        Dengan Sopir
                    </span>
                    <span class="font-bold text-gray-900 dark:text-white">Rp {{ formatCurrency(mobil.mobil_harga_include) }}</span>
                </div>
            </div>

            <!-- Buttons -->
            <div class="grid grid-cols-2 gap-3 pt-2">
                <Link :href="`/mobil/${mobil.mobil_slug}`" class="flex items-center justify-center px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-bold transition-all rounded-lg">
                    Detail <i class="fa fa-arrow-right ml-2 text-xs"></i>
                </Link>
                <a :href="waLink" target="_blank" class="flex items-center justify-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white text-sm font-bold transition-all rounded-lg shadow-md hover:shadow-lg">
                    <i class="fa fa-whatsapp mr-1.5 text-lg"></i> Booking
                </a>
            </div>
        </div>
    </div>
</template>
